import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import useStore from '../../store/useStore';
import { datasetsAPI } from '../../services/api';
import { readSheet, suggestColumns, titleFromFileName } from '../../utils/datasetUpload';
import { CADENCE_LABEL, COLUMN_TYPES, COLUMN_TYPE_LABEL, datasetPath, formatNumber, toIdentifier } from './dataSourceFormat';
import '../../styles/DatasetManagement.css';
import '../../styles/DataSources.css';
import '../../styles/DatasetBuilder.css';

const IDENTIFIER_RE = /^[a-z][a-z0-9_]*$/;
const NAME_RULE = 'Use lowercase letters, digits and underscores, starting with a letter.';
const describeError = (err, fallback) => err?.response?.data?.error || err?.message || fallback;
let nextKey = 1;
const newColumn = (c = {}) => ({ key: nextKey++, name: '', type: 'string', required: false, description: '', ...c });
const newJoin = (j = {}) => ({ key: nextKey++, column: '', dataset: '', refColumn: '', ...j });

/**
 * Problems with the definition, keyed by where they show: name, a column's
 * key, or a join's key. Empty when the definition can be saved.
 */
export function definitionErrors({ name, columns, relationships }) {
  const errors = { columns: {}, joins: {} };
  if (!name) errors.name = 'Give the dataset a name.';
  else if (!IDENTIFIER_RE.test(name)) errors.name = NAME_RULE;
  if (columns.length === 0) errors.noColumns = 'Add at least one column.';
  const seen = new Set();
  for (const c of columns) {
    if (!c.name) errors.columns[c.key] = 'Name this column.';
    else if (!IDENTIFIER_RE.test(c.name)) errors.columns[c.key] = NAME_RULE;
    else if (seen.has(c.name)) errors.columns[c.key] = `Another column is already called ${c.name}.`;
    seen.add(c.name);
  }
  for (const r of relationships) {
    if (!r.column || !r.dataset || !r.refColumn) errors.joins[r.key] = 'Choose both columns and the dataset, or remove this link.';
    else if (!seen.has(r.column)) errors.joins[r.key] = `There is no column called ${r.column} any more.`;
  }
  return errors;
}

export const hasErrors = (e) => Boolean(e.name || e.noColumns || Object.keys(e.columns).length || Object.keys(e.joins).length);

/**
 * Create a dataset, or edit one the company defines: what it is called, its
 * columns (start from a spreadsheet to have them suggested), which column
 * identifies a row, and links to other datasets so reports can combine them.
 */
const DatasetDefinitionPage = () => {
  const { name: editing } = useParams();
  const isEdit = Boolean(editing);
  const navigate = useNavigate();
  const companyId = useStore((state) => state.currentCompany?.id);

  const [title, setTitle] = useState('');
  const [name, setName] = useState('');
  const [nameEdited, setNameEdited] = useState(false);
  const [description, setDescription] = useState('');
  const [columns, setColumns] = useState([newColumn()]);
  const [primaryKey, setPrimaryKey] = useState('');
  const [cadence, setCadence] = useState('none');
  const [relationships, setRelationships] = useState([]);
  const [datasets, setDatasets] = useState([]);
  const [targets, setTargets] = useState({});
  const [loading, setLoading] = useState(true);
  const [locked, setLocked] = useState('');
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sourceFile, setSourceFile] = useState(null);
  const [suggested, setSuggested] = useState(null);

  /** Another dataset's columns, with types, for the link pickers. */
  const loadTarget = useCallback(async (dataset) => {
    if (!dataset || targets[dataset] || !companyId) return;
    try {
      const res = await datasetsAPI.getSchema(dataset, companyId);
      setTargets((t) => ({ ...t, [dataset]: { title: res.data.title || null, columns: res.data.columns.map((c) => ({ name: c.name, type: c.type })) } }));
    } catch {
      setTargets((t) => ({ ...t, [dataset]: { title: null, columns: [] } }));
    }
  }, [companyId, targets]);

  useEffect(() => {
    if (!companyId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const list = await datasetsAPI.list(companyId);
        if (cancelled) return;
        setDatasets((list?.success ? list.data : []).map((d) => ({ name: d.name, title: d.title || null })));
        if (isEdit) {
          const d = (await datasetsAPI.getSchema(editing, companyId)).data;
          if (cancelled) return;
          if (d.definitionSource === 'pack') {
            setLocked(`${editing} is defined by ${d.packTitle ? `the ${d.packTitle} pack` : 'its pack'}, which keeps its columns up to date. You can add your own measures to it on its page.`);
          }
          setTitle(d.title || '');
          setName(d.name);
          setDescription(d.description || '');
          setColumns(d.columns.map((c) => newColumn({ name: c.name, type: c.type, required: Boolean(c.required), description: c.description || '' })));
          setPrimaryKey(d.primaryKey || '');
          setCadence(d.cadence || 'none');
          setRelationships((d.relationships || []).map((r) => newJoin({ column: r.column, dataset: r.refDataset, refColumn: r.refColumn })));
        }
      } catch (err) {
        if (!cancelled) setError(describeError(err, 'Could not load the dataset'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [companyId, editing, isEdit]);

  // Column details for every dataset a link points at.
  useEffect(() => {
    for (const r of relationships) if (r.dataset) loadTarget(r.dataset);
  }, [relationships, loadTarget]);

  const errors = useMemo(() => definitionErrors({ name, columns, relationships }), [name, columns, relationships]);
  const show = (message) => (submitted ? message : null);
  const namedColumns = columns.filter((c) => IDENTIFIER_RE.test(c.name));
  const otherDatasets = datasets.filter((d) => d.name !== name);

  const onTitle = (value) => {
    setTitle(value);
    if (!isEdit && !nameEdited) setName(toIdentifier(value));
  };

  const fromSpreadsheet = async (file) => {
    if (!file) return;
    try {
      setError('');
      const sheet = await readSheet(await file.arrayBuffer());
      const cols = suggestColumns(sheet);
      if (cols.length === 0) throw new Error('The first row of the file has no column names.');
      setColumns(cols.map((c) => newColumn(c)));
      setPrimaryKey('');
      if (!title) onTitle(titleFromFileName(file.name));
      setSourceFile(file);
      setSuggested({ fileName: file.name, rows: sheet.rows.length, columns: cols.length });
    } catch (err) {
      setError(describeError(err, 'Could not read the file'));
    }
  };

  const updateColumn = (key, patch) => {
    setColumns((cols) => {
      const before = cols.find((c) => c.key === key);
      if (patch.name !== undefined && before && before.name && primaryKey === before.name) setPrimaryKey(patch.name);
      return cols.map((c) => (c.key === key ? { ...c, ...patch } : c));
    });
  };
  const moveColumn = (key, by) => setColumns((cols) => {
    const i = cols.findIndex((c) => c.key === key);
    const j = i + by;
    if (j < 0 || j >= cols.length) return cols;
    const next = [...cols];
    [next[i], next[j]] = [next[j], next[i]];
    return next;
  });
  const removeColumn = (key) => {
    const col = columns.find((c) => c.key === key);
    if (col && col.name === primaryKey) setPrimaryKey('');
    setColumns((cols) => cols.filter((c) => c.key !== key));
  };
  const updateJoin = (key, patch) => setRelationships((rels) => rels.map((r) => (r.key === key ? { ...r, ...patch } : r)));

  const save = async (e) => {
    e.preventDefault();
    setSubmitted(true);
    if (hasErrors(errors)) {
      setError('Some details need fixing; they are marked below.');
      return;
    }
    const body = {
      ...(isEdit ? {} : { name }),
      title: title.trim() || null,
      description: description.trim() || null,
      columns: columns.map((c) => ({ name: c.name, type: c.type, required: c.required || c.name === primaryKey, description: c.description.trim() || null })),
      primaryKey: primaryKey || null,
      cadence,
      relationships: relationships.map((r) => ({ column: r.column, references: `${r.dataset}.${r.refColumn}` })),
    };
    try {
      setSaving(true);
      setError('');
      if (isEdit) await datasetsAPI.updateDefinition(editing, companyId, body);
      else await datasetsAPI.define(companyId, body);
      navigate(datasetPath(name), {
        state: {
          notice: isEdit ? 'Definition saved.' : `${title.trim() || name} created.`,
          ...(sourceFile && !isEdit ? { uploadFile: sourceFile } : {}),
        },
      });
    } catch (err) {
      setError(describeError(err, 'Could not save the dataset'));
    } finally {
      setSaving(false);
    }
  };

  const datasetLabel = (d) => (d.title ? `${d.title} (${d.name})` : d.name);

  if (loading) return <div className="dataset-management"><div className="loading-spinner" /></div>;

  return (
    <div className="dataset-management data-source-detail dataset-builder">
      <nav className="breadcrumb" aria-label="Breadcrumb">
        <Link to="/admin/data-sources">Data sources</Link> /{' '}
        {isEdit ? <><Link to={datasetPath(editing)}>{editing}</Link> / Edit definition</> : <><Link to="/admin/data-sources/add">Add data source</Link> / Dataset</>}
      </nav>
      <div className="page-header">
        <div>
          <h2>{isEdit ? `Edit ${editing}` : 'New dataset'}</h2>
          <p className="page-subtitle">
            {isEdit
              ? 'You can always add columns. A column can be removed or change type only when no measure, report or KPI uses it.'
              : 'Describe the data first. The dataset starts empty; you then upload a spreadsheet or push rows to it.'}
          </p>
        </div>
      </div>

      {locked ? <div className="error-message" role="alert">{locked}</div> : (
        <form onSubmit={save} noValidate>
          {error && <div className="error-message" role="alert">{error}</div>}

          {!isEdit && (
            <section className="builder-start" aria-label="How to start">
              <label className="builder-start-option">
                <input type="file" accept=".csv,.xlsx,.xls" className="visually-hidden" onChange={(e) => fromSpreadsheet(e.target.files?.[0])} aria-label="Start from a spreadsheet" />
                <strong>Start from a spreadsheet</strong>
                <span>Choose a CSV or Excel file. Its first row becomes the columns and the types are suggested from the rows below.</span>
              </label>
              <div className="builder-start-option builder-start-option--static">
                <strong>Or start from scratch</strong>
                <span>Add the columns yourself below.</span>
              </div>
            </section>
          )}
          {suggested && (
            <div className="success-message" role="status">
              {suggested.columns} columns suggested from {suggested.fileName} ({formatNumber(suggested.rows)} rows). Check the names and types;
              once the dataset is created you can upload this file in one step.
            </div>
          )}

          <div className="builder-layout">
            <div className="builder-main">
              <section className="source-card" aria-labelledby="builder-about">
                <h3 id="builder-about">About this dataset</h3>
                <div className="definition-fields">
                  <label>
                    <span>Title</span>
                    <input value={title} onChange={(e) => onTitle(e.target.value)} placeholder="Sales targets" />
                  </label>
                  <label>
                    <span>Name</span>
                    <input
                      value={name}
                      onChange={(e) => { setName(e.target.value); setNameEdited(true); }}
                      onBlur={() => setName((n) => toIdentifier(n) || n)}
                      disabled={isEdit}
                      aria-label="Dataset name"
                      aria-invalid={Boolean(show(errors.name))}
                      aria-describedby="builder-name-hint"
                    />
                    <small id="builder-name-hint" className={show(errors.name) ? 'field-error' : ''}>
                      {show(errors.name) || (isEdit ? 'The name cannot change.' : 'Used by reports and the API. Filled in from the title.')}
                    </small>
                  </label>
                  <label className="definition-wide">
                    <span>Description</span>
                    <input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="What one row is, and where the data comes from" />
                  </label>
                  <label>
                    <span>New data expected</span>
                    <select value={cadence} onChange={(e) => setCadence(e.target.value)} aria-describedby="builder-cadence-hint">
                      {['none', 'hourly', 'daily', 'weekly', 'monthly'].map((c) => <option key={c} value={c}>{c === 'none' ? 'No schedule' : CADENCE_LABEL[c]}</option>)}
                    </select>
                    <small id="builder-cadence-hint">The dataset is marked overdue when new data is late.</small>
                  </label>
                </div>
              </section>

              <section className="source-card" aria-labelledby="builder-columns">
                <div className="builder-section-head">
                  <h3 id="builder-columns">Columns</h3>
                  <span className="source-hint">{columns.length} column{columns.length === 1 ? '' : 's'}</span>
                </div>
                <div className="builder-key-none">
                  <label>
                    <input type="radio" name="dataset-key" checked={!primaryKey} onChange={() => setPrimaryKey('')} />
                    No key: every upload or push adds or replaces rows
                  </label>
                </div>
                {show(errors.noColumns) && <p className="field-error">{errors.noColumns}</p>}
                <ol className="builder-columns">
                  {columns.map((c, i) => {
                    const message = show(errors.columns[c.key]);
                    const isKey = Boolean(c.name) && primaryKey === c.name;
                    return (
                      <li key={c.key} className={`builder-column${message ? ' has-error' : ''}`}>
                        <div className="builder-column-order">
                          <button type="button" onClick={() => moveColumn(c.key, -1)} disabled={i === 0} aria-label={`Move ${c.name || `column ${i + 1}`} up`}>↑</button>
                          <button type="button" onClick={() => moveColumn(c.key, 1)} disabled={i === columns.length - 1} aria-label={`Move ${c.name || `column ${i + 1}`} down`}>↓</button>
                        </div>
                        <label className="builder-column-name">
                          <span>Name</span>
                          <input
                            value={c.name}
                            onChange={(e) => updateColumn(c.key, { name: e.target.value })}
                            onBlur={() => updateColumn(c.key, { name: toIdentifier(c.name) || c.name })}
                            aria-label={`Column ${i + 1} name`}
                            aria-invalid={Boolean(message)}
                          />
                        </label>
                        <label>
                          <span>Type</span>
                          <select value={c.type} onChange={(e) => updateColumn(c.key, { type: e.target.value })} aria-label={`Column ${i + 1} type`}>
                            {COLUMN_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                          </select>
                        </label>
                        <label className="builder-column-description">
                          <span>Description</span>
                          <input value={c.description} onChange={(e) => updateColumn(c.key, { description: e.target.value })} aria-label={`Column ${i + 1} description`} placeholder="Optional" />
                        </label>
                        <div className="builder-column-flags">
                          <label title="Every row must have a value">
                            <input type="checkbox" checked={c.required || isKey} disabled={isKey} onChange={(e) => updateColumn(c.key, { required: e.target.checked })} aria-label={`Column ${i + 1} required`} />
                            Required
                          </label>
                          <label title="Identifies each row, so new data updates rows instead of adding them again">
                            <input type="radio" name="dataset-key" checked={isKey} disabled={!c.name} onChange={() => setPrimaryKey(c.name)} aria-label={`Column ${i + 1} is the key`} />
                            Key
                          </label>
                        </div>
                        <button type="button" className="builder-remove" onClick={() => removeColumn(c.key)} aria-label={`Remove ${c.name || `column ${i + 1}`}`}>×</button>
                        {message && <p className="field-error builder-column-error">{message}</p>}
                      </li>
                    );
                  })}
                </ol>
                <button type="button" className="btn-secondary" onClick={() => setColumns((cols) => [...cols, newColumn()])}>+ Add column</button>
              </section>

              <section className="source-card" aria-labelledby="builder-links">
                <h3 id="builder-links">Links to other datasets</h3>
                <p className="source-hint" style={{ marginTop: 0 }}>
                  Link a column to a matching column in another dataset, so one report can combine them. For example, link each target&apos;s owner to the owner on HubSpot deals.
                </p>
                {relationships.map((r, i) => {
                  const ownType = columns.find((c) => c.name === r.column)?.type;
                  const target = targets[r.dataset];
                  const targetType = target?.columns.find((c) => c.name === r.refColumn)?.type;
                  const message = show(errors.joins[r.key]);
                  return (
                    <div key={r.key} className={`builder-link${message ? ' has-error' : ''}`}>
                      <span>Each row&apos;s</span>
                      <select value={r.column} onChange={(e) => updateJoin(r.key, { column: e.target.value })} aria-label={`Link ${i + 1}: column in this dataset`}>
                        <option value="">column…</option>
                        {namedColumns.map((c) => <option key={c.key} value={c.name}>{c.name}</option>)}
                      </select>
                      <span>matches</span>
                      <select
                        value={r.dataset}
                        onChange={(e) => updateJoin(r.key, { dataset: e.target.value, refColumn: '' })}
                        aria-label={`Link ${i + 1}: other dataset`}
                      >
                        <option value="">dataset…</option>
                        {otherDatasets.map((d) => <option key={d.name} value={d.name}>{datasetLabel(d)}</option>)}
                      </select>
                      <span aria-hidden="true">›</span>
                      <select
                        value={r.refColumn}
                        onChange={(e) => updateJoin(r.key, { refColumn: e.target.value })}
                        disabled={!r.dataset}
                        aria-label={`Link ${i + 1}: column in the other dataset`}
                      >
                        <option value="">column…</option>
                        {(target?.columns || []).map((c) => <option key={c.name} value={c.name}>{c.name} ({COLUMN_TYPE_LABEL[c.type]})</option>)}
                      </select>
                      <button type="button" className="builder-remove" onClick={() => setRelationships((rels) => rels.filter((x) => x.key !== r.key))} aria-label={`Remove link ${i + 1}`}>×</button>
                      {message && <p className="field-error builder-link-note">{message}</p>}
                      {!message && ownType && targetType && ownType !== targetType && (
                        <p className="builder-link-note builder-link-warning">
                          {r.column} is {COLUMN_TYPE_LABEL[ownType].toLowerCase()} and {r.refColumn} is {COLUMN_TYPE_LABEL[targetType].toLowerCase()}. Rows link only when the values are the same.
                        </p>
                      )}
                    </div>
                  );
                })}
                {otherDatasets.length === 0
                  ? <p className="source-hint">There are no other datasets to link to yet.</p>
                  : <button type="button" className="btn-secondary" onClick={() => setRelationships((rels) => [...rels, newJoin()])}>+ Link to another dataset</button>}
              </section>
            </div>

            <aside className="builder-summary" aria-label="Summary">
              <h3>{title.trim() || name || 'New dataset'}</h3>
              {name && <code>{name}</code>}
              <dl>
                <div><dt>Columns</dt><dd>{columns.length}</dd></div>
                <div><dt>Key</dt><dd>{primaryKey || 'None'}</dd></div>
                <div><dt>Links</dt><dd>{relationships.filter((r) => r.column && r.dataset && r.refColumn).length}</dd></div>
                <div><dt>Expected</dt><dd>{cadence === 'none' ? 'No schedule' : CADENCE_LABEL[cadence]}</dd></div>
              </dl>
              {namedColumns.length > 0 && (
                <ul className="builder-summary-columns">
                  {namedColumns.map((c) => (
                    <li key={c.key}>
                      <span>{c.name}</span>
                      <span className="builder-type">{COLUMN_TYPE_LABEL[c.type]}{c.name === primaryKey ? ' · key' : c.required ? ' · required' : ''}</span>
                    </li>
                  ))}
                </ul>
              )}
              {!isEdit && (
                <p className="source-hint">
                  Next: {sourceFile ? `upload ${sourceFile.name}` : 'upload a spreadsheet'} or push rows through the API, then build measures, reports and KPIs on it.
                </p>
              )}
              <div className="builder-submit">
                <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create dataset'}</button>
                <Link to={isEdit ? datasetPath(editing) : '/admin/data-sources/add'}>Cancel</Link>
              </div>
            </aside>
          </div>
        </form>
      )}
    </div>
  );
};

export default DatasetDefinitionPage;
